/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeButton,
  DimeCard,
  DimeCardContent,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeCollapsible,
  DimeCollapsibleContent,
  DimeCollapsibleTrigger,
  DimeField,
  DimeFieldGroup,
  DimeFieldLabel,
  DimeInput,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, File, Folder, Maximize, Minimize } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/collapsible')({
  component: CollapsibleDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

// --- Basic ---

function BasicExample() {
  return (
    <DimeCard className="mx-auto w-full max-w-sm">
      <DimeCardContent className="pt-6">
        <DimeCollapsible className="rounded-md data-[state=open]:bg-muted">
          <DimeCollapsibleTrigger asChild>
            <DimeButton variant="ghost" className="group w-full">
              Product details
              <ChevronDown className="ml-auto transition-transform group-data-[state=open]:rotate-180" />
            </DimeButton>
          </DimeCollapsibleTrigger>
          <DimeCollapsibleContent className="flex flex-col items-start gap-2 p-2.5 pt-0 text-sm">
            <div>This panel can be expanded or collapsed to reveal additional content.</div>
            <DimeButton size="xs">Learn More</DimeButton>
          </DimeCollapsibleContent>
        </DimeCollapsible>
      </DimeCardContent>
    </DimeCard>
  );
}

// --- Settings panel ---

function SettingsExample() {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <DimeCard className="mx-auto w-full max-w-xs">
      <DimeCardHeader>
        <DimeCardTitle>Radius</DimeCardTitle>
        <DimeCardDescription>Set the corner radius of the element.</DimeCardDescription>
      </DimeCardHeader>
      <DimeCardContent>
        <DimeCollapsible open={isOpen} onOpenChange={setIsOpen} className="flex items-start gap-2">
          <DimeFieldGroup className="grid w-full grid-cols-2 gap-2">
            <DimeField>
              <DimeFieldLabel htmlFor="radius-tl" className="sr-only">
                Top-left
              </DimeFieldLabel>
              <DimeInput id="radius-tl" placeholder="0" defaultValue="0" />
            </DimeField>
            <DimeField>
              <DimeFieldLabel htmlFor="radius-tr" className="sr-only">
                Top-right
              </DimeFieldLabel>
              <DimeInput id="radius-tr" placeholder="0" defaultValue="0" />
            </DimeField>
            <DimeCollapsibleContent className="col-span-full grid grid-cols-2 gap-2">
              <DimeField>
                <DimeFieldLabel htmlFor="radius-bl" className="sr-only">
                  Bottom-left
                </DimeFieldLabel>
                <DimeInput id="radius-bl" placeholder="0" defaultValue="0" />
              </DimeField>
              <DimeField>
                <DimeFieldLabel htmlFor="radius-br" className="sr-only">
                  Bottom-right
                </DimeFieldLabel>
                <DimeInput id="radius-br" placeholder="0" defaultValue="0" />
              </DimeField>
            </DimeCollapsibleContent>
          </DimeFieldGroup>
          <DimeCollapsibleTrigger asChild>
            <DimeButton variant="outline" size="icon" aria-label={isOpen ? 'Collapse' : 'Expand'}>
              {isOpen ? <Minimize /> : <Maximize />}
            </DimeButton>
          </DimeCollapsibleTrigger>
        </DimeCollapsible>
      </DimeCardContent>
    </DimeCard>
  );
}

// --- File tree ---

type FileTreeItem = { name: string } | { name: string; items: FileTreeItem[] };

const FILE_TREE: FileTreeItem[] = [
  {
    name: 'components',
    items: [
      {
        name: 'ui',
        items: [
          { name: 'button.tsx' },
          { name: 'card.tsx' },
          { name: 'dialog.tsx' },
          { name: 'input.tsx' },
          { name: 'select.tsx' },
          { name: 'table.tsx' },
        ],
      },
      { name: 'login-form.tsx' },
      { name: 'register-form.tsx' },
    ],
  },
  {
    name: 'lib',
    items: [{ name: 'utils.ts' }, { name: 'cn.ts' }, { name: 'api.ts' }],
  },
  {
    name: 'hooks',
    items: [
      { name: 'use-media-query.ts' },
      { name: 'use-debounce.ts' },
      { name: 'use-local-storage.ts' },
    ],
  },
  { name: 'app.tsx' },
  { name: 'layout.tsx' },
  { name: 'globals.css' },
];

function FileTreeItem({ item }: Readonly<{ item: FileTreeItem }>) {
  if ('items' in item) {
    return (
      <DimeCollapsible>
        <DimeCollapsibleTrigger asChild>
          <DimeButton
            variant="ghost"
            size="sm"
            className="group w-full justify-start transition-none"
          >
            <ChevronRight className="transition-transform group-data-[state=open]:rotate-90" />
            <Folder />
            {item.name}
          </DimeButton>
        </DimeCollapsibleTrigger>
        <DimeCollapsibleContent className="ml-5 mt-1">
          <div className="flex flex-col gap-1">
            {item.items.map((child) => (
              <FileTreeItem key={child.name} item={child} />
            ))}
          </div>
        </DimeCollapsibleContent>
      </DimeCollapsible>
    );
  }
  return (
    <DimeButton variant="ghost" size="sm" className="w-full justify-start gap-2 text-foreground">
      <File />
      <span>{item.name}</span>
    </DimeButton>
  );
}

function FileTreeExample() {
  return (
    <DimeCard className="mx-auto w-full max-w-[16rem]">
      <DimeCardHeader>
        <DimeCardTitle>Explorer</DimeCardTitle>
      </DimeCardHeader>
      <DimeCardContent>
        <div className="flex flex-col gap-1">
          {FILE_TREE.map((item) => (
            <FileTreeItem key={item.name} item={item} />
          ))}
        </div>
      </DimeCardContent>
    </DimeCard>
  );
}

// --- Page ---

function CollapsibleDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Collapsible</h1>
      <p className="mb-8 text-muted-foreground">
        Toggles a region of content open and closed. Compose with{' '}
        <code className="font-mono text-sm">DimeCollapsibleTrigger</code> and{' '}
        <code className="font-mono text-sm">DimeCollapsibleContent</code>.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <BasicExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeCollapsibleTrigger asChild</code> wraps any element as the
          toggle. The collapsible applies <code className="font-mono">data-[state=open]</code>{' '}
          classes you can target with Tailwind.
        </p>
      </DocsSection>

      <DocsSection title="Settings panel">
        <Preview>
          <SettingsExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Compose <code className="font-mono">DimeCollapsible</code> with{' '}
          <code className="font-mono">DimeCard</code> and{' '}
          <code className="font-mono">DimeField</code> to build expandable form sections. The
          trigger can be any element — here an icon button controls the open state externally.
        </p>
      </DocsSection>

      <DocsSection title="File tree">
        <Preview>
          <FileTreeExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Nest <code className="font-mono">DimeCollapsible</code> recursively to build tree
          structures. Each folder node is its own collapsible with a{' '}
          <code className="font-mono">ChevronRight</code> that rotates on open.
        </p>
      </DocsSection>
    </div>
  );
}
